import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronUp, Plus, Calendar, Star } from 'lucide-react';
import TimelineItem from './TimelineItem';
import AddItemMenu from './AddItemMenu';

const DayCard = ({
    day,
    items,
    isExpanded,
    onToggle,
    viewMode,
    onAddItem,
    onRemoveItem,
    onTimeChange,
    isAddMenuOpen,
    setIsAddMenuOpen
}) => {
    const { setNodeRef, isOver } = useDroppable({
        id: day.toString(),
    });

    const isEnrollmentDay = day === 0;
    const itemIds = items.map(item => item.id);

    const getDayLabel = () => {
        if (day === 0) return 'Day 0';
        if (day < 0) return `Day ${day}`;
        return `Day ${day}`;
    };

    const getDayDescription = () => {
        if (day === 0) return 'Enrollment / Start Date';
        if (day === 1) return 'First Day';
        if (day === 7) return 'One Week';
        if (day === 14) return 'Two Weeks';
        if (day === 21) return 'Three Weeks';
        if (day === 30) return 'One Month';
        if (day < 0) return `${Math.abs(day)} days before start`;
        return `${day} days after start`;
    };

    return (
        <div
            ref={setNodeRef}
            className={`
                bg-white rounded-xl border-2 transition-all overflow-hidden
                ${isEnrollmentDay ? 'border-primary shadow-lg shadow-primary/10' : 'border-gray-200'}
                ${isOver ? 'border-primary bg-primary/5' : ''}
            `}
        >
            {/* Day Header */}
            <button
                onClick={onToggle}
                className={`
                    w-full px-4 py-3 flex items-center justify-between transition-colors
                    ${isEnrollmentDay ? 'bg-primary/5' : 'hover:bg-gray-50'}
                `}
            >
                <div className="flex items-center gap-3">
                    <div className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        ${isEnrollmentDay
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-600'
                        }
                    `}>
                        {isEnrollmentDay ? (
                            <Star className="w-5 h-5" />
                        ) : (
                            <Calendar className="w-5 h-5" />
                        )}
                    </div>
                    <div className="text-left">
                        <div className="flex items-center gap-2">
                            <h3 className={`font-bold ${isEnrollmentDay ? 'text-primary' : 'text-gray-800'}`}>
                                {getDayLabel()}
                            </h3>
                            {isEnrollmentDay && (
                                <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                                    Anchor
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500">{getDayDescription()}</p>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-500">
                        {items.length} item{items.length !== 1 ? 's' : ''}
                    </span>
                    {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                </div>
            </button>

            {/* Day Content - Collapsible */}
            {isExpanded && (
                <div className="border-t border-gray-100 p-4">
                    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                        {items.length === 0 ? (
                            <div className="py-8 text-center text-gray-400">
                                <p className="text-sm">No items scheduled for this day</p>
                                <p className="text-xs mt-1">Click "Add Item" to schedule content</p>
                            </div>
                        ) : (
                            <div className={`space-y-2 ${viewMode === 'compact' ? 'space-y-1' : 'space-y-3'}`}>
                                {items.map((item) => (
                                    <TimelineItem
                                        key={item.id}
                                        item={item}
                                        viewMode={viewMode}
                                        onRemove={() => onRemoveItem(item.id)}
                                        onTimeChange={onTimeChange}
                                    />
                                ))}
                            </div>
                        )}
                    </SortableContext>

                    {/* Add Item Button */}
                    <div className="mt-4 relative">
                        <button
                            onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
                            className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                            <Plus className="w-4 h-4" />
                            Add Item
                        </button>

                        {isAddMenuOpen && (
                            <AddItemMenu
                                onSelect={onAddItem}
                                onClose={() => setIsAddMenuOpen(false)}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DayCard;
